import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'gatsby';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Image from '../../../../components/shared/image';
import { useHeaderMenuBackdrop } from '../../header-menu-context';
import { useProductTree } from '../../../../graphql/hooks/use-product-tree';
import { usePathname } from '../../../../lib/use-pathname';
import {
  categoryPathToUrl,
  getChildrenForPath,
  getExpandedDefaultPath,
  getMenuPathPrefix,
  getNodeByPath,
  productPathToUrl,
  type ProductTreeNode,
} from '../../../../lib/product-tree';

function getRelativeActivePath(
  activePath: string[],
  pathPrefix: string[],
): string[] {
  if (
    pathPrefix.length &&
    pathPrefix.every((slug, i) => activePath[i] === slug)
  ) {
    return activePath.slice(pathPrefix.length);
  }
  return activePath;
}

type MegaMenuItemProps = {
  isActive: boolean;
  showChevron: boolean;
  to: string;
  onMouseEnter: () => void;
  onClick: () => void;
  children: string;
};

const MegaMenuItem: FC<MegaMenuItemProps> = ({
  isActive,
  showChevron,
  to,
  onMouseEnter,
  onClick,
  children,
}) => (
  <Link
    className={`product-mega-menu__item${isActive ? ' product-mega-menu__item--active' : ''}`}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    to={to}
  >
    <span aria-hidden className="product-mega-menu__item-bar" />
    <span className="product-mega-menu__item-label">{children}</span>
    {showChevron ? (
      <ChevronRight
        aria-hidden
        className="product-mega-menu__item-chevron"
        size={20}
        strokeWidth={2}
      />
    ) : null}
  </Link>
);

function categoryHasNextStep(
  node: ProductTreeNode,
  productsByCategorySlug: Map<string, { slug: string }[]>,
): boolean {
  if (node.children.length > 0) return true;
  return (productsByCategorySlug.get(node.slug) ?? []).length > 0;
}

function getDefaultProductSlug(
  path: string[],
  roots: ProductTreeNode[],
  productsByCategorySlug: Map<string, { slug: string }[]>,
): string {
  const node = getNodeByPath(roots, path);
  if (!node || node.children.length > 0) return '';
  return productsByCategorySlug.get(node.slug)?.[0]?.slug ?? '';
}

function expandMenuPath(
  basePath: string[],
  roots: ProductTreeNode[],
  productsByCategorySlug: Map<string, { slug: string }[]>,
): { activePath: string[]; productSlug: string } {
  const activePath = getExpandedDefaultPath(roots, basePath);
  return {
    activePath,
    productSlug: getDefaultProductSlug(activePath, roots, productsByCategorySlug),
  };
}

const ProductsNav: FC = () => {
  const pathname = usePathname();
  const { roots, productsByCategorySlug } = useProductTree();
  const [show, setShow] = useState(false);
  useHeaderMenuBackdrop(show);
  const productsActive =
    pathname === '/products' || pathname.startsWith('/products/');
  const [activePath, setActivePath] = useState<string[]>([]);
  const [hoveredProductSlug, setHoveredProductSlug] = useState('');

  const pathPrefix = useMemo(() => getMenuPathPrefix(roots), [roots]);

  useEffect(() => {
    if (roots.length && !activePath.length) {
      const { activePath: nextPath, productSlug } = expandMenuPath(
        pathPrefix,
        roots,
        productsByCategorySlug,
      );
      setActivePath(nextPath);
      setHoveredProductSlug(productSlug);
    }
  }, [roots, activePath.length, pathPrefix, productsByCategorySlug]);

  const wasMenuOpen = useRef(false);

  /** Reset drill-down only when the menu opens (not on every render while open). */
  useEffect(() => {
    if (show && !wasMenuOpen.current) {
      const { activePath: nextPath, productSlug } = expandMenuPath(
        pathPrefix,
        roots,
        productsByCategorySlug,
      );
      setActivePath(nextPath);
      setHoveredProductSlug(productSlug);
    }
    wasMenuOpen.current = show;
  }, [show, pathPrefix, roots, productsByCategorySlug]);

  const activeNode = useMemo(
    () => getNodeByPath(roots, activePath),
    [roots, activePath],
  );

  const menuColumns = useMemo(() => {
    const relativePath = getRelativeActivePath(activePath, pathPrefix);
    const columns: {
      header: string;
      headerLink?: string;
      nodes: ProductTreeNode[];
      depth: number;
    }[] = [];

    for (let depth = 0; depth <= relativePath.length; depth += 1) {
      const parentPath = [...pathPrefix, ...relativePath.slice(0, depth)];
      const children = getChildrenForPath(roots, parentPath);
      if (!children.length) break;

      const parentNode = getNodeByPath(roots, parentPath);
      const header =
        depth === 0
          ? 'MACHINES'
          : (parentNode?.menuLabel ?? parentNode?.title ?? '');

      columns.push({
        header,
        headerLink: categoryPathToUrl(parentPath),
        nodes: children,
        depth,
      });
    }

    return columns;
  }, [roots, activePath, pathPrefix]);

  const leafCategorySlug = activeNode?.slug ?? '';
  const leafProducts = productsByCategorySlug.get(leafCategorySlug) ?? [];
  const showProductColumn =
    activeNode && !activeNode.children.length && leafProducts.length > 0;

  const activeProductSlug = useMemo(() => {
    if (!showProductColumn || !leafProducts.length) return '';
    if (
      hoveredProductSlug &&
      leafProducts.some((product) => product.slug === hoveredProductSlug)
    ) {
      return hoveredProductSlug;
    }
    return leafProducts[0]?.slug ?? '';
  }, [showProductColumn, leafProducts, hoveredProductSlug]);

  const previewProduct = leafProducts.find(
    (product) => product.slug === activeProductSlug,
  );
  const previewImageUrl =
    previewProduct?.headerImageUrl ||
    previewProduct?.thumbnailUrl ||
    activeNode?.previewImageUrl ||
    '';

  const previewTitle =
    previewProduct?.title ?? activeNode?.title ?? 'Product';

  const previewLink = previewProduct
    ? productPathToUrl(previewProduct.categoryPath, previewProduct.slug)
    : activeNode
      ? categoryPathToUrl(activePath)
      : '/products';

  // Show gradient preview card whenever a leaf product is selected
  // (media optional — card still shows title on brand gradient).
  const showPreviewColumn = Boolean(previewProduct) || Boolean(previewImageUrl);

  const closeMenu = () => setShow(false);

  if (!roots.length) {
    return (
      <li className="site-header__nav-item">
        <Link
          className={`site-header__nav-link${productsActive ? ' site-header__nav-link--active' : ''}`}
          to="/products"
        >
          <span>PRODUCTS</span>
        </Link>
      </li>
    );
  }

  return (
    <li
      className="site-header__nav-item"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Link
        className={`site-header__nav-link site-header__nav-link--has-menu${show || productsActive ? ' site-header__nav-link--active' : ''}`}
        to="/products"
      >
        <span>PRODUCTS</span>
        <ChevronDown
          aria-hidden
          className={`site-header__nav-chevron${show ? ' site-header__nav-chevron--open' : ''}`}
          size={16}
          strokeWidth={2}
        />
      </Link>

      <AnimatePresence>
        {show ? (
          <motion.div
            animate={{ opacity: 1 }}
            className={`product-mega-menu site-header__mega-menu fixed left-0 right-0 z-40 w-full border-b border-t border-gray-200 shadow-lg${showPreviewColumn ? ' product-mega-menu--preview-open' : ''}`}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <div className="product-mega-menu__columns mx-auto flex min-h-[20rem] w-full max-w-[2100px] items-stretch">
              {menuColumns.map(({ header, headerLink, nodes, depth }) => (
                <div
                  className={`product-mega-menu__column product-mega-menu__column--depth-${depth}`}
                  key={`${header}-${depth}`}
                >
                  {headerLink ? (
                    <Link
                      className="product-mega-menu__header product-mega-menu__header--link"
                      onClick={closeMenu}
                      to={headerLink}
                    >
                      {header}
                    </Link>
                  ) : (
                    <p className="product-mega-menu__header">{header}</p>
                  )}
                  <div className="product-mega-menu__list">
                    {nodes.map((node) => {
                      const pathAtColumn = [
                        ...pathPrefix,
                        ...getRelativeActivePath(activePath, pathPrefix).slice(
                          0,
                          depth,
                        ),
                        node.slug,
                      ];
                      const relative = getRelativeActivePath(
                        activePath,
                        pathPrefix,
                      );
                      const isActive = relative[depth] === node.slug;

                      return (
                        <MegaMenuItem
                          isActive={isActive}
                          key={node.slug}
                          showChevron={categoryHasNextStep(
                            node,
                            productsByCategorySlug,
                          )}
                          onClick={closeMenu}
                          onMouseEnter={() => {
                            const { activePath: nextPath, productSlug } =
                              expandMenuPath(
                                pathAtColumn,
                                roots,
                                productsByCategorySlug,
                              );
                            setActivePath(nextPath);
                            setHoveredProductSlug(productSlug);
                          }}
                          to={categoryPathToUrl(pathAtColumn)}
                        >
                          {node.menuLabel}
                        </MegaMenuItem>
                      );
                    })}
                  </div>
                </div>
              ))}

              {showProductColumn ? (
                <div className="product-mega-menu__column product-mega-menu__column--products">
                  <Link
                    className="product-mega-menu__header product-mega-menu__header--link"
                    onClick={closeMenu}
                    to={categoryPathToUrl(activePath)}
                  >
                    {activeNode?.title ?? 'MODELS'}
                  </Link>
                  <div className="product-mega-menu__list">
                    {leafProducts.map((product) => {
                      return (
                        <MegaMenuItem
                          isActive={activeProductSlug === product.slug}
                          key={product.slug}
                          onClick={closeMenu}
                          onMouseEnter={() => setHoveredProductSlug(product.slug)}
                          showChevron
                          to={productPathToUrl(
                            product.categoryPath,
                            product.slug,
                          )}
                        >
                          {product.title}
                        </MegaMenuItem>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {showPreviewColumn ? (
                <div className="product-mega-menu__column product-mega-menu__preview">
                  <p
                    aria-hidden
                    className="product-mega-menu__header product-mega-menu__header--spacer"
                  >
                    &nbsp;
                  </p>
                  <div className="product-mega-menu__preview-body">
                    <Link
                      className="product-mega-menu__preview-card"
                      onClick={closeMenu}
                      to={previewLink}
                    >
                      <div className="product-mega-menu__preview-media">
                        {previewImageUrl ? (
                          <Image
                            alt={previewTitle}
                            className="product-mega-menu__preview-img"
                            height={340}
                            key={previewImageUrl}
                            src={previewImageUrl}
                            width={280}
                          />
                        ) : null}
                      </div>
                      <p className="product-mega-menu__preview-title">
                        {previewTitle}
                      </p>
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </li>
  );
};

export default ProductsNav;
